import { useState, useEffect } from 'react';
import { API } from '../Services/axios';
import { MagentoAPI } from '../Services/magentoApi';
import { URLS } from '../Services/urls';

export const useProductDetails = (productId) => {
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productId) return;

    const fetchProduct = async () => {
      setIsLoading(true);
      setError(null);
      
      // Utiliser directement l'endpoint qui fonctionne
      try {
        console.log(`Récupération du produit ${productId} via l'API`);
        const response = await API.get(`product/${productId}/`);
        console.log(`✅ Succès API:`, response.data);
        setProduct(response.data);
        setIsLoading(false);
        return;
      } catch (err) {
        console.log(`❌ Échec API:`, err.response?.status || err.message);
        // Si l'endpoint principal échoue, essayer les autres
      }

      // Fallback: essayer d'autres endpoints si le principal échoue
      const fallbackEndpoints = [
        `products/${productId}/`,
        `magento/product/${productId}/`,
        `catalog/product/${productId}/`
      ];

      for (const endpoint of fallbackEndpoints) {
        try {
          console.log(`Tentative fallback avec l'endpoint: ${endpoint}`);
          const response = await API.get(endpoint);
          console.log(`✅ Succès fallback avec ${endpoint}:`, response.data);
          setProduct(response.data);
          setIsLoading(false);
          return;
        } catch (err) {
          console.log(`❌ Échec fallback avec ${endpoint}:`, err.response?.status || err.message);
          continue;
        }
      }
      
      // Si tous les endpoints échouent
      console.error('Tous les endpoints ont échoué pour le produit:', productId);
      setError(new Error('Aucun endpoint valide trouvé'));
      setIsLoading(false);
    };

    fetchProduct();
  }, [productId]);

  return { product, isLoading, error };
};

export const useMultipleProductDetails = (productIds) => {
  const [products, setProducts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productIds || productIds.length === 0) return;

    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const promises = productIds.map(async (productId) => {
          try {
            const response = await API.get(URLS.PRODUCT.getProductById(productId));
            return { id: productId, data: response.data };
          } catch (err) {
            console.error(`Erreur lors de la récupération du produit ${productId}:`, err);
            return { id: productId, data: null, error: err };
          }
        });

        const results = await Promise.all(promises);
        const productsMap = {};
        results.forEach(({ id, data, error }) => {
          productsMap[id] = { data, error };
        });
        
        setProducts(productsMap);
      } catch (err) {
        console.error('Erreur lors de la récupération des produits:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [productIds]);

  return { products, isLoading, error };
};
